/**
 * Linear Assets — Vegetation Encroachment Projection (Step 3 & 4)
 *
 * Capabilities:
 *   • Predictive days-to-contact model from NDVI growth rates
 *   • Sequential kilometre marker segmentation
 *   • Human decision sign-off gate
 */

import React, { useState, useEffect } from 'react';
import {
  Zap, AlertTriangle, ShieldCheck, Clock,
  ArrowRight, Download, Filter, MapPin, Check
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import { linearAssetsApi, reportsApi } from '../../services/api';

export const LinearEncroachmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [projections, setProjections] = useState<any[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    linearAssetsApi.getEncroachmentProjections()
      .then((res) => setProjections(res.data?.projections || []))
      .catch((err) => setError(err?.message || 'Failed to load projections'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-500" /> Vegetation Encroachment Projections (Step 3–4)
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            NDVI delta growth rates segmented by kilometre markers — projecting days-to-contact with power line &amp; rail safety envelopes
          </p>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <div className="space-y-4">
        {projections.map((p) => {
          const isCritical = p.days_to_contact <= 45;
          const isHigh = p.days_to_contact <= 90 && !isCritical;
          return (
            <Card key={p.corridor_id} className={`p-5 border-l-4 ${isCritical ? 'border-red-500' : isHigh ? 'border-amber-500' : 'border-blue-500'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-base text-gray-900 dark:text-white">{p.corridor_name}</h3>
                    <span className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] ${
                      isCritical ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                      isHigh ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {p.risk_level} Risk
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-amber-500" /> Sector: <strong>Km {p.start_km} &ndash; Km {p.end_km}</strong> ({p.length_m} metres)
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/linear-assets/survey-planner')}
                    className="flex items-center gap-1"
                  >
                    Schedule Drone Survey (Step 5) <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs mb-3">
                <div>
                  <span className="text-gray-400 block">Current Clearance</span>
                  <span className="font-bold text-gray-900 dark:text-white">{p.current_distance_to_centreline_m} m</span>
                  <span className="text-[10px] text-gray-400"> (Buffer: {p.buffer_threshold_m}m)</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Growth Velocity</span>
                  <span className="font-bold text-amber-600">+{p.monthly_growth_rate_m} m/month</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Projected Contact</span>
                  <span className="font-bold text-red-600">{p.days_to_contact} Days ({p.projected_contact_date})</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Est. Canopy Height</span>
                  <span className="font-bold text-gray-900 dark:text-white">{p.canopy_height_est_m} m AGL</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default LinearEncroachmentPage;
